/**
 * This component
 * 1. Manages checkout state with a reducer
 * 2. Provides state and dispatch contexts to components below
 * 3. Takes children as a prop so you can pass JSX to it
 */

import React, {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useReducer,
} from "react"

import { checkoutReducer } from "./checkoutReducer"
import { State, Action, CartItem, Currency } from "../types/checkoutTypes"
import { getLs } from "../../utils/localStorage"
import {
  getCartProduct,
  getStripeId,
  LoadProductPrices,
} from "../../utils/stripejs"
import { useProducts } from "../../hooks/useProducts"

const cartItems: CartItem[] = (getLs("cartItems") as CartItem[]) || []
const currency: Currency = (getLs("currency") as Currency) || "usd"
const countryInventory = getLs("country_for_inventory") as string

const checkoutInitialState: State = {
  cartItems,
  currentStep: 0,
  currency,
  shipping: undefined,
  taxes: undefined,
  couponDiscount: 0,
  couponCode: "",
  couponDescription: "",
  accountRequired: false,
  isGift: false,
  shippingNotes: "",
  countryInventory,
  couponDiscountDays: 0,
  grantsLinkedUserMembershipTrialDays: 0,
  total: 0,
  subTotal: 0,
  isChecoutInitiated: false,
  promptAddToCart: [],
}

const CheckoutContext = createContext<State>(checkoutInitialState)
const CheckoutDispatchContext = createContext<Dispatch<Action>>(() => {})

export const CheckoutProvider = ({ children }: { children: ReactNode }) => {
  LoadProductPrices()
  const products = useProducts()

  const UsageItems = new Set<string>([])
  products.forEach(product => {
    if (product.hasSeats()) {
      if (product.stripeSeatProduct) {
        UsageItems.add(product.stripeSeatProduct.id)
      }
    }
  })

  //make sure these items still exist

  checkoutInitialState.cartItems = checkoutInitialState.cartItems.filter(
    item => {
      const stripeProd = getCartProduct(item.productId)
      if (!stripeProd) {
        return false
      }
      if (UsageItems.has(stripeProd.id)) {
        return true
      }
      const product = products.filter(prd => {
        const stripeId = getStripeId(prd.stripeId, prd.stripeTestId)
        return stripeId === stripeProd.id
      })
      return product.length > 0
    }
  )

  const [checkoutState, checkoutDispatch] = useReducer(
    checkoutReducer,
    checkoutInitialState
  )

  return (
    <CheckoutContext.Provider value={checkoutState}>
      <CheckoutDispatchContext.Provider value={checkoutDispatch}>
        {children}
      </CheckoutDispatchContext.Provider>
    </CheckoutContext.Provider>
  )
}

export function useCheckoutState() {
  return useContext(CheckoutContext)
}

export function useCheckoutDispatch() {
  return useContext(CheckoutDispatchContext)
}
